import { List } from 'immutable';
import { calculateBackoffTime } from 'flow/utils/socket-utils';
import { getFaro } from 'flow/api-clients/faro-client';
import {
    getUserAgent,
    startCall,
    endCall,
} from 'flow/api-clients/meeting-call-client';
import {
    MeetingLogLevel,
    addMeetingLog,
    stripSenderNameUuidPrefix,
} from 'flow/utils/meeting-utils';
import {
    getRttMeetingValues,
    rtcStateChangeEventHandler,
    checkDisconnectedConnectionState,
    checkFailedConnectionState,
} from 'flow/utils/rtc-utils';
import {
    selectActiveMeetingCode,
    selectActiveMeetingMonitorInterval,
    selectActiveMeetingReconnectAttempt,
    selectSenderAudioActive,
    selectSenderName,
    selectVideoErrorShow,
    selectVideoInUse,
    selectActiveMeetingCallSession,
    selectLobbyAudioActive,
    selectActiveMeetingCallConnectionState,
    selectHasNoRtcConnection,
    selectMeetingIsActive,
    selectVideoConferenceConfig,
} from 'flow/selectors/meeting-selectors';
import {
    createVideoChatWebSocket,
    closeVideoChatWebSocket,
} from 'flow/api-clients/video-chat-client';
import {
    muteAudio,
    setActiveMeetingCallSessionAction,
    setActiveMeetingCallSessionConnectionStateAction,
    setActiveMeetingMembers,
    setActiveMeetingMonitorAction,
    setMeetingErrorAction,
    setActiveMeetingReconnectAttemptAction,
    setupInitialTrackStatus,
    setSenderAudioStream,
    setupSenderVideoStream,
    linkAudioDeviceIdToAudioReference,
    setActiveMeetingReconnectActiveAction,
} from 'flow/actions/meeting-actions';
import {
    createVideoSignalingWebSocket,
    joinVideoRoom,
    leaveVideoRoom,
    sendMessage,
} from 'flow/api-clients/video-signaling-client';
import {
    setVideoConnectionStateAction,
    setVideoSenderScreenShareActiveAction,
} from 'flow/actions/video-actions';
import {
    selectOwnWaitingRoomStanding,
    selectVideoConnectionState,
    selectVideoSenderPrimaryPeer,
    selectVideoSenderScreenShareActive,
    selectVideoSenderVideoActive,
    selectVideoWebSocket,
    selectVideoSenderReconnectionToken,
    selectWebsocketIsClosed,
    selectWebsocketReadyState,
} from 'flow/selectors/video-selectors';
import {
    incrementDisconnectCount,
    stopMetricsCollection,
    resetDisconnectCount,
} from 'flow/utils/video-log-utils';

export const startMeeting = () => async (dispatch, getState) => {
    const userAgent = await dispatch(getUserAgent());
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingSupervisor#startMeeting starting meeting, starting call for ${senderName}, using own userAgent ${!!userAgent}`,
        ),
    );

    resetDisconnectCount();

    // Join the room before placing the audio call (VID-425). Meeting audio is a
    // separate SIP leg to the conference bridge, so starting it first put a
    // participant into the meeting — audible, and hearing everyone — while
    // admission (VID-76) was still being decided about them. joinVideoRoom stays
    // unresolved for as long as we are held, which is exactly the wait we want
    // the audio call to sit behind.
    //
    // A join that fails for any other reason is deliberately NOT fatal: the
    // meeting has always degraded to audio-only when video cannot be set up, and
    // that is worth keeping. Only being turned away stops us below.
    let roomJoined = false;
    dispatch(
        addMeetingLog(
            'MeetingSupervisor#startMeeting starting meeting, joining video room',
        ),
    );
    try {
        await dispatch(createVideoSignalingWebSocket());
        await dispatch(joinVideoRoom());
        roomJoined = true;
    } catch (e) {
        dispatch(
            addMeetingLog(
                `MeetingSupervisor#startMeeting could not join the video room, ${
                    e?.message ?? e
                }\n${e?.stack ?? ''}`,
                MeetingLogLevel.ERROR,
            ),
        );
    }

    // Any standing at the door ends the join here rather than falling through to
    // the audio call. A refusal that still puts them on the bridge is not a
    // refusal, and neither is a wait: while held the join above simply does not
    // resolve, but it can also be abandoned (see awaitingApproval), and that
    // must not read as permission to dial.
    if (selectOwnWaitingRoomStanding(getState())) {
        dispatch(
            addMeetingLog(
                'MeetingSupervisor#startMeeting held at or turned away from the waiting room, not placing the audio call',
            ),
        );
        return;
    }

    if (userAgent) {
        try {
            const callSession = await dispatch(startCall(userAgent));
            dispatch(linkAudioDeviceIdToAudioReference());
            const audioActive = selectLobbyAudioActive(getState());
            if (!audioActive) {
                dispatch(
                    addMeetingLog(
                        'MeetingSupervisor#startMeeting audio should be muted, muting it',
                    ),
                );
                dispatch(muteAudio());
            }
            dispatch(setActiveMeetingCallSessionAction(callSession));
            dispatch(setSenderAudioStream());

            const rtcPeerConnection = callSession && callSession.connection;
            dispatch(
                rtcStateChangeEventHandler(
                    rtcPeerConnection,
                    setActiveMeetingCallSessionConnectionStateAction,
                ),
            );
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingSupervisor#startMeeting got an error when starting meeting ${
                        e && e.message
                    }`,
                    MeetingLogLevel.ERROR,
                ),
            );
            throw new Error('Could not setup call');
        }
    } else {
        dispatch(
            addMeetingLog(
                'MeetingSupervisor#startMeeting can not start meeting without user agent',
                MeetingLogLevel.ERROR,
            ),
        );
        throw new Error('No user agent');
    }

    dispatch(
        addMeetingLog(
            'MeetingSupervisor#startMeeting starting meeting, starting video',
        ),
    );
    try {
        // Guarded on the join above: without a room there is nothing to publish
        // to, and this block used to be skipped by the same failure landing in
        // one shared catch.
        if (!roomJoined) {
            throw new Error('not joined to the video room');
        }
        await dispatch(setupInitialTrackStatus());
        const isVideoActive = selectVideoSenderVideoActive(getState());
        if (isVideoActive) {
            const videoDeviceToUse = selectVideoInUse(getState());
            await dispatch(setupSenderVideoStream(videoDeviceToUse));
        }
        const roomId = selectActiveMeetingCode(getState());
        const chatSenderName = stripSenderNameUuidPrefix(senderName);
        const videoChatBaseUrl =
            selectVideoConferenceConfig(getState())?.videoChatUrl;
        if (roomId && chatSenderName) {
            try {
                await dispatch(
                    createVideoChatWebSocket(
                        roomId,
                        chatSenderName,
                        videoChatBaseUrl,
                    ),
                );
            } catch (chatErr) {
                dispatch(
                    addMeetingLog(
                        `MeetingSupervisor#startMeeting video-chat WS failed: ${chatErr?.message || chatErr}`,
                        MeetingLogLevel.WARNING,
                    ),
                );
            }
        }
    } catch (e) {
        dispatch(
            addMeetingLog(
                `MeetingSupervisor#startMeeting could not start meeting, ${
                    e?.message ?? e
                }\n${e?.stack ?? ''}`,
                MeetingLogLevel.ERROR,
            ),
        );

        // TODO: #video handle KMS signaling not set up
        // TODO: #video if the sender peer could not set up
        // TODO: #video handle roomNotJoined from KMS
    }

    dispatch(startMeetingMonitor());
};

export const stopMeeting = () => async (dispatch, getState) => {
    dispatch(
        addMeetingLog(
            'MeetingSupervisor#stopMeeting clear interval, leave room and end call',
        ),
    );
    const monitorInterval = selectActiveMeetingMonitorInterval(getState());
    clearInterval(monitorInterval);

    stopMetricsCollection();

    dispatch(leaveVideoRoom());
    dispatch(closeVideoChatWebSocket());
    try {
        await dispatch(endCall());
    } catch (_InvalidStateError) {
        dispatch(
            addMeetingLog('MeetingSupervisor#stopMeeting could not end call'),
        );
    }
    dispatch(closeWebsocket());
};

const startMeetingMonitor = () => (dispatch, getState) => {
    dispatch(
        addMeetingLog(
            'MeetingSupervisor#startMeetingMonitor starting meeting monitor',
        ),
    );
    const intervalTimer = 2000;
    let interval = null;

    let videoRTTList = [];
    let rttDiffList = [];
    const VIDEO_RTT_LIST_MAX_SIZE = 10;
    const RTT_COMPARISON_MAX_MS = 300;
    let notifyDesynchronization = false;

    const monitor = async () => {
        const state = getState();
        const meetingIsActive = selectMeetingIsActive(state);
        if (!meetingIsActive) {
            dispatch(
                addMeetingLog(
                    'MeetingSupervisor#monitor: No active meeting, cancel monitor',
                ),
            );
            clearInterval(interval);
            return;
        }

        const showingError = selectVideoErrorShow(state);

        const socketIsClosed = selectWebsocketIsClosed(getState());
        const hasNoRtcConnection = selectHasNoRtcConnection(getState());

        const videoConnectionState = selectVideoConnectionState(state);
        const callConnectionState =
            selectActiveMeetingCallConnectionState(state);
        const websocketState = selectWebsocketReadyState(state);

        const rtcDisconnected =
            checkDisconnectedConnectionState(callConnectionState) ||
            checkDisconnectedConnectionState(videoConnectionState);

        if (socketIsClosed || hasNoRtcConnection) {
            incrementDisconnectCount();
            const attempt = selectActiveMeetingReconnectAttempt(state);
            dispatch(
                addMeetingLog(
                    `MeetingSupervisor#monitor: Monitor detected error - attempt: ${attempt}, videoConnectionState: ${videoConnectionState}, callConnectionState: ${callConnectionState}, websocketState: ${websocketState}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            if (attempt === 0) {
                await dispatch(startReconnectWithErrorPrompt());
            }
        } else if (rtcDisconnected) {
            // RTC connections is in a disconnected state but can reconnect automatically
            incrementDisconnectCount();
            dispatch(
                addMeetingLog(
                    'MeetingSupervisor#monitor: rtcDisconnected, showing reconnect message',
                    MeetingLogLevel.WARNING,
                ),
            );
            dispatch(
                setMeetingErrorAction(
                    true,
                    'videoconference.error.network_reconnect',
                    true,
                    false,
                ),
            );
        } else if (showingError) {
            dispatch(
                addMeetingLog(
                    'MeetingSupervisor#monitor: Removing reconnect message',
                ),
            );
            dispatch(setMeetingErrorAction(false, null, false, false));
            dispatch(setActiveMeetingReconnectActiveAction(false));
        }

        const videoSenderPrimaryPeer = selectVideoSenderPrimaryPeer(state);
        const callSession = selectActiveMeetingCallSession(state);
        const { videoRtt, audioVideoRttDiff } = await getRttMeetingValues(
            videoSenderPrimaryPeer,
            callSession,
        );
        videoRTTList.push(videoRtt);
        if (audioVideoRttDiff || audioVideoRttDiff === 0) {
            rttDiffList.push(audioVideoRttDiff);
        }

        const getAverage = (list) =>
            list.reduce((a, b) => a + b, 0) / list.length;
        const convertListEntriesToMilliseconds = (list) =>
            list.map((x) => parseInt(('' + x * 1000).slice(0, 4)));
        const getListStatistics = (list) => {
            const msList = convertListEntriesToMilliseconds(list);
            const average = getAverage(msList);
            const max = Math.max(...msList);
            const min = Math.min(...msList);
            return { average, max, min };
        };

        if (videoRTTList.length === VIDEO_RTT_LIST_MAX_SIZE) {
            const { average, max, min } = getListStatistics(videoRTTList);
            dispatch(
                addMeetingLog(
                    `MeetingSupervisor#monitor: local video RTT, average: ${average}ms\tmin: ${min}ms\tmax: ${max}ms`,
                ),
            );
            videoRTTList = [];
        }

        if (rttDiffList.length === VIDEO_RTT_LIST_MAX_SIZE) {
            const { average, max, min } = getListStatistics(rttDiffList);
            dispatch(
                addMeetingLog(
                    `MeetingSupervisor#monitor: A/V RTT diff, average: ${average}ms\tmin: ${min}ms\tmax: ${max}ms`,
                ),
            );
            rttDiffList = [];

            const largestDiff = max > Math.abs(min) ? max : Math.abs(min);
            if (
                largestDiff > RTT_COMPARISON_MAX_MS &&
                !notifyDesynchronization
            ) {
                notifyDesynchronization = true;
                // Too large A/V desync, report to investigate
                getFaro()?.api.pushError(
                    new Error('Meeting audio and video desync too large'),
                    { context: { diff: String(largestDiff) } },
                );
            }
        }
    };

    interval = setInterval(monitor, intervalTimer);
    dispatch(setActiveMeetingMonitorAction(interval));
};

const startReconnectWithErrorPrompt = () => async (dispatch) => {
    dispatch(setActiveMeetingReconnectActiveAction(true));
    dispatch(addMeetingLog('MeetingSupervisor#startReconnectWithErrorPrompt'));
    dispatch(
        setMeetingErrorAction(
            true,
            'videoconference.error.network_reconnect',
            true,
            false,
        ),
    );
    dispatch(setActiveMeetingReconnectAttemptAction(1));
    await dispatch(reconnectAttempt(1));
};

export const initiateReconnect = () => async (dispatch) => {
    dispatch(addMeetingLog('MeetingSupervisor#initiateReconnect'));
    await dispatch(startReconnectWithErrorPrompt());
};

const reconnectAttempt = (attempt) => async (dispatch, getState) => {
    const RECONNECT_TRIES = 4;
    if (!attempt) {
        dispatch(
            addMeetingLog(
                'MeetingSupervisor#reconnectAttempt trying to reconnect without attempt count - aborting',
            ),
            MeetingLogLevel.ERROR,
        );
        return;
    }
    dispatch(
        addMeetingLog(
            `MeetingSupervisor#reconnectAttempt on attempt ${attempt}`,
        ),
    );
    if (attempt <= RECONNECT_TRIES) {
        try {
            await dispatch(reconnect());
            // Guard against a reconnect that resolved without actually
            // restoring the underlying connection — otherwise we reset the
            // attempt counter to 0 and the monitor immediately starts a new
            // reconnect, producing an infinite "Trying to reconnect" loop.
            const stateAfterReconnect = getState();
            if (
                selectWebsocketIsClosed(stateAfterReconnect) ||
                selectHasNoRtcConnection(stateAfterReconnect)
            ) {
                throw new Error(
                    'Reconnect resolved but connection is still broken',
                );
            }
            dispatch(setMeetingErrorAction(false, null, false, false));
            dispatch(setActiveMeetingReconnectAttemptAction(0));
            dispatch(setActiveMeetingReconnectActiveAction(false));
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingSupervisor#reconnectAttempt error on reconnect: ${JSON.stringify(
                        e,
                    )}`,
                ),
                MeetingLogLevel.ERROR,
            );
            const nextAttempt = attempt + 1;
            dispatch(setActiveMeetingReconnectAttemptAction(nextAttempt));
            if (attempt < RECONNECT_TRIES) {
                const backoffTime = calculateBackoffTime(attempt);
                dispatch(
                    addMeetingLog(
                        `MeetingSupervisor#reconnectAttempt trying a reconnect in ${backoffTime}ms`,
                    ),
                );
                setTimeout(
                    async () => await dispatch(reconnectAttempt(nextAttempt)),
                    backoffTime,
                );
            } else {
                dispatch(
                    setMeetingErrorAction(
                        true,
                        'videoconference.error.network_try_again',
                        false,
                        true,
                    ),
                );
                dispatch(setActiveMeetingReconnectActiveAction(false));
            }
        }
    } else {
        dispatch(setActiveMeetingReconnectActiveAction(false));
        dispatch(
            setMeetingErrorAction(
                true,
                'videoconference.error.network_try_again',
                false,
                true,
            ),
        );
    }
};

const closeWebsocket = () => (dispatch, getState) => {
    dispatch(
        addMeetingLog('MeetingSupervisor#closeWebsocket closing websocket'),
    );
    const websocket = selectVideoWebSocket(getState());
    if (websocket) {
        websocket.close();
    }
};

const reconnect = () => async (dispatch, getState) => {
    const senderName = selectSenderName(getState());
    const videoActive = selectVideoSenderVideoActive(getState());
    const screenShareActive = selectVideoSenderScreenShareActive(getState());
    // The reconnect path runs whenever the monitor sees either the video signaling WS
    // closed or any RTC peer failed. SIP audio and video signaling are independent, so
    // when the SIP audio RTC peer is still connected we keep the existing call session
    // instead of forcing a fresh INVITE — re-INVITEs frequently hit JsSIP Timer B (32s)
    // when the original socket-drop event also affected outbound SIP, which produced a
    // very visible "audio dies during reconnect" symptom on UAE meetings.
    // 'completed' is the healthy terminal ICE state Firefox reports (it falls back to
    // oniceconnectionstatechange in rtc-utils); guard on the session existing too so we
    // never trust a stale connection state after the session has already gone.
    const audioPeerHealthy =
        !!selectActiveMeetingCallSession(getState()) &&
        ['connected', 'completed'].includes(
            selectActiveMeetingCallConnectionState(getState()),
        );
    dispatch(
        addMeetingLog(
            `MeetingSupervisor#reconnect reconnecting ${senderName}, audioPeerHealthy: ${audioPeerHealthy}`,
        ),
    );

    // Remove meeting members
    dispatch(setActiveMeetingMembers(new List()));

    if (!audioPeerHealthy) {
        // Try to leave the call
        try {
            await dispatch(endCall());
        } catch (_InvalidStateError) {
            dispatch(
                addMeetingLog('MeetingSupervisor#reconnect could not end call'),
            );
        }
    }

    // Try to "leave" the video room
    dispatch(leaveVideoRoom());

    // Kill the websocket
    dispatch(closeWebsocket());

    const oldSenderPeer = selectVideoSenderPrimaryPeer(getState());
    if (oldSenderPeer) {
        dispatch(
            addMeetingLog(
                `MeetingSupervisor#reconnect disposing peer of ${senderName}`,
            ),
        );
        oldSenderPeer.dispose();
    } else {
        dispatch(
            addMeetingLog(
                `MeetingSupervisor#reconnect cant dispose peer of ${senderName} - no peer found`,
            ),
        );
    }

    const endCallIfNoActiveMeeting = async () => {
        const isActiveMeeting = selectMeetingIsActive(getState());
        if (!isActiveMeeting) {
            try {
                await dispatch(endCall());
                dispatch(
                    addMeetingLog(
                        'MeetingSupervisor#endCallIfNotActiveMeeting no active meeting when reconnecting, ending call',
                        MeetingLogLevel.WARNING,
                    ),
                );
                return true;
            } catch (_InvalidStateError) {
                dispatch(
                    addMeetingLog(
                        'MeetingSupervisor#endCallIfNotActiveMeeting could not end call',
                    ),
                );
            }
        }
        return false;
    };

    if (audioPeerHealthy) {
        // SIP audio peer is still healthy; check that the user hasn't left the meeting
        // in the meantime, then keep the existing call session.
        const endedCall = await endCallIfNoActiveMeeting();
        if (endedCall) {
            dispatch(
                addMeetingLog(
                    'MeetingSupervisor#reconnect no active meeting when reconnecting',
                ),
            );
            return;
        }
        dispatch(
            addMeetingLog(
                'MeetingSupervisor#reconnect keeping existing SIP audio session',
            ),
        );
    } else {
        // Start call again
        try {
            const userAgent = await dispatch(getUserAgent());
            const callSession = await dispatch(startCall(userAgent));
            const endedCall = await endCallIfNoActiveMeeting();
            if (endedCall) {
                dispatch(
                    addMeetingLog(
                        'MeetingSupervisor#reconnect no active meeting when reconnecting',
                    ),
                );
                return;
            }

            const audioActive = selectSenderAudioActive(getState());
            if (!audioActive) {
                dispatch(muteAudio());
            }
            const rtcPeerConnection = callSession && callSession.connection;
            dispatch(
                rtcStateChangeEventHandler(
                    rtcPeerConnection,
                    setActiveMeetingCallSessionConnectionStateAction,
                ),
            );
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingSupervisor#reconnect could not start new call ${
                        e && e.message
                    }`,
                    MeetingLogLevel.ERROR,
                ),
            );
            throw new Error('Could not start call');
        }
    }

    // Re-establish the websocket
    await dispatch(createVideoSignalingWebSocket());

    const roomId = selectActiveMeetingCode(getState());
    const chatSenderName = stripSenderNameUuidPrefix(senderName);
    const videoChatBaseUrl =
        selectVideoConferenceConfig(getState())?.videoChatUrl;
    if (roomId && chatSenderName) {
        try {
            dispatch(closeVideoChatWebSocket());
            await dispatch(
                createVideoChatWebSocket(
                    roomId,
                    chatSenderName,
                    videoChatBaseUrl,
                ),
            );
        } catch (chatErr) {
            dispatch(
                addMeetingLog(
                    `MeetingSupervisor#reconnect video-chat WS failed: ${chatErr?.message || chatErr}`,
                    MeetingLogLevel.WARNING,
                ),
            );
        }
    }

    // Re-join video with rejoin code
    const reconnectionToken = selectVideoSenderReconnectionToken(getState());
    await dispatch(joinVideoRoom(reconnectionToken));

    const setupVideo = async () => {
        const videoDeviceInUse = selectVideoInUse(getState());
        await dispatch(setupSenderVideoStream(videoDeviceInUse));
        const senderPrimaryPeer = selectVideoSenderPrimaryPeer(getState());
        // Need to tell KMS that there is a new video endpoint that the media server needs to establish
        dispatch(sendMessage({ id: 'restartVideo', sender: senderName }));

        const senderRtcPeerConnection =
            senderPrimaryPeer && senderPrimaryPeer.peerConnection;
        dispatch(
            rtcStateChangeEventHandler(
                senderRtcPeerConnection,
                setVideoConnectionStateAction,
            ),
        );
    };

    if (screenShareActive) {
        // TODO: #video inform user that reconnected disabled screenshare
        dispatch(setVideoSenderScreenShareActiveAction(false));
        dispatch(
            sendMessage({ id: 'trackDisabled', kind: 'screenShareActive' }),
        );
    }
    if (videoActive) {
        await setupVideo();
    } else {
        dispatch(sendMessage({ id: 'trackDisabled', kind: 'video' }));
    }

    // audioPeerHealthy was sampled once, at the top, to decide whether to keep the
    // existing SIP session. When we kept it, that session's peer is never disposed or
    // recreated here, so its callConnectionState stays live (its original handler keeps
    // updating it) and is safe to re-read — unlike the freshly-built video/audio peers,
    // whose store state is stale until their handlers first fire. If the kept session
    // died while we were re-establishing video (e.g. one network blip took down both
    // channels and the SIP failure event only landed now), throw so reconnectAttempt
    // retries: the next attempt sees an unhealthy audio peer and does a full re-INVITE,
    // rather than clearing the error banner while audio is actually dead.
    if (
        audioPeerHealthy &&
        checkFailedConnectionState(
            selectActiveMeetingCallConnectionState(getState()),
        )
    ) {
        dispatch(
            addMeetingLog(
                'MeetingSupervisor#reconnect kept SIP session failed during reconnect, forcing re-INVITE on retry',
                MeetingLogLevel.WARNING,
            ),
        );
        throw new Error('Kept SIP audio session failed during reconnect');
    }
};
